import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from './useAuth';

interface Farm {
  id: string;
  name: string;
  location?: string;
}

interface FarmsResponse {
  data: Farm[];
  meta: {
    total: number;
  };
}

async function fetchFarms(): Promise<Farm[]> {
  const response = await api.get<FarmsResponse>('/api/v1/farms');
  return response.data;
}

export function useFarms() {
  const { session, user } = useAuth();

  // Scope the cache by user id so a sign-out/sign-in as a different user doesn't serve stale farms.
  const { data: farms, isLoading, error, status, fetchStatus, refetch } = useQuery({
    queryKey: ['farms', user?.id],
    queryFn: fetchFarms,
    enabled: !!session && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // A different user's farms must never stand in while this user's load.
    placeholderData: undefined,
  });

  return {
    farms: farms || [],
    isLoading,
    error,
    /** The farm list is known (loaded or failed); false while pending or signed out. */
    isSettled: status !== 'pending',
    isSuccess: status === 'success',
    /** No farm list and the request failed. */
    isFailed: status === 'error' && !farms,
    /** No farm list and the request is paused for lack of a connection. */
    isOffline: status === 'pending' && fetchStatus === 'paused',
    refetch,
  };
}
